import i18n from '../../../../i18n';
import { routeReplace } from '../../../../lib/browser';
import {
  appendDepositAppQuery,
  getDepositAppQuery,
  getDepositEntry,
  leaveToApp,
} from '../h5-deposit/appBridge';
import {
  accountNumberOf,
  accountSubUserId,
  getMarketBankCache,
  isSecuritiesAccount,
  localizedBankName,
  matchDepositAccount,
  preferredAccountNum,
} from '../h5-deposit/shared';

export type Currency = 'HKD' | 'USD' | 'CNY';

/** 提交成功后标记：再进出金页则直接离开，避免回到已填表单或空 /account */
const WITHDRAW_DONE_KEY = 'h5-withdraw-done';

export const entryAccountNum = () => String(getDepositAppQuery().accountNum || '').trim();

const withAppQuery = (path: string, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams();
  Object.entries(extra).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  appendDepositAppQuery(params);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

export const withdrawRecordsUrl = (accountNum: string, opts?: { isBackToApp?: boolean }) => {
  const entry = getDepositEntry();
  return withAppQuery('/h5/fund-records', {
    type: 'withdraw',
    accountNum,
    ...(entry.mode ? { mode: entry.mode } : {}),
    ...(entry.modeType ? { modeType: entry.modeType } : {}),
    ...(opts?.isBackToApp ? { isBackToApp: '1' } : {}),
  });
};

export const withdrawPageUrl = () => withAppQuery('/h5/withdrawals');

export const markWithdrawDone = () => {
  try {
    sessionStorage.setItem(WITHDRAW_DONE_KEY, '1');
  } catch {
    // ignore
  }
};

export const shouldExitWithdraw = () => {
  try {
    return sessionStorage.getItem(WITHDRAW_DONE_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearWithdrawDone = () => {
  try {
    sessionStorage.removeItem(WITHDRAW_DONE_KEY);
  } catch {
    // ignore
  }
};

/** 出金完成离开：App 回原生（带 backToAppUrl）；浏览器尽量 history.back */
export const leaveWithdraw = () => {
  clearWithdrawDone();
  leaveToApp();
};

/**
 * 成功后进记录：用资金记录替换当前出金表单 history，带 isBackToApp=1。
 * 不再垫一层空白出金页（旧 h5-withdraw-done 方案）。
 */
export const leaveToRecords = (accountNum: string) => {
  routeReplace(withdrawRecordsUrl(accountNum, { isBackToApp: true }));
};

export const bankIdentity = (row: any) => `${String(row?.bankCode ?? '')}|${String(row?.bankAccountNum ?? '')}`;

/** 和入金同一套：mode 滤证券/虚拟，modeType 不参与过滤。没有 mode 时人民币只留证券户 */
export const withdrawVisibleAccounts = (list: any[], currency: Currency, mode: string) =>
  list.filter((row) => {
    if (!matchDepositAccount(row, mode)) return false;
    if (!mode && currency === 'CNY' && !isSecuritiesAccount(row)) return false;
    return true;
  });

/** 和入金同一套：URL 账户，再按 modeType，再现金户/融资户，最后第一项 */
export const pickWithdrawAccount = (list: any[]) => {
  const url = entryAccountNum();
  const byUrl = url ? list.find((row) => accountNumberOf(row) === url) : null;
  const entry = getDepositEntry();
  const byType =
    entry.mode === 'security' && (entry.modeType === 'C' || entry.modeType === 'M')
      ? list.find((row) => String(row?.type ?? '').trim() === entry.modeType)
      : null;
  const perm = preferredAccountNum('');
  const byPerm = perm ? list.find((row) => accountNumberOf(row) === perm) : null;
  return byUrl || byType || byPerm || list[0] || null;
};

/** 本地化银行名；有账号再接括号。字典没有就用接口名，不单独请求 */
export const withdrawBankLine = (row: any) => {
  const name = localizedBankName(row, getMarketBankCache());
  const num = String(row?.bankAccountNum ?? '').trim();
  if (name && num) return `${name} (${num})`;
  return name || num;
};

export const applyAccount = (row: any | null, accountNum = '') => {
  if (!row) return { accountNum, subUserID: '' };
  return { accountNum: accountNumberOf(row), subUserID: accountSubUserId(row) };
};

/** 所选账户里、币种等于当前 currency 的 withdrawalBal。没有则空，不要写成 0 */
export const withdrawalBalanceOf = (row: any, currency: string) => {
  const list = Array.isArray(row?.currencies)
    ? row.currencies
    : Array.isArray(row?.stockAccounts)
      ? row.stockAccounts
      : [];
  const want = String(currency ?? '')
    .trim()
    .toUpperCase();
  const hit = list.find((item) => String(item?.currency ?? '').trim().toUpperCase() === want);
  if (!hit || hit.withdrawalBal == null || hit.withdrawalBal === '') return '';
  return hit.withdrawalBal;
};

export const isNumericBalance = (value: unknown) => {
  if (value == null || value === '') return false;
  return Number.isFinite(Number(value));
};

/** 只留数字和一个小数点；整数最多 15 位；小数最多 3 位；开头的点补成 0. */
export const sanitizeWithdrawAmount = (input: string) => {
  let text = String(input ?? '').replace(/[^\d.]/g, '');
  if (!text) return '';
  const dot = text.indexOf('.');
  let intPart = dot >= 0 ? text.slice(0, dot) : text;
  const decPart = dot >= 0 ? text.slice(dot + 1).replace(/\./g, '') : null;
  if (dot === 0) intPart = '0';
  intPart = intPart.replace(/^0+(?=\d)/, '');
  if (!intPart) intPart = '0';
  if (intPart.length > 15) intPart = intPart.slice(0, 15);
  if (decPart == null) return intPart;
  return `${intPart}.${decPart.slice(0, 3)}`;
};

/** 空或数值 0 为「免费」；否则原样，币种空则用当前出金币种 */
export const feeTextOf = (amount: unknown, chargeCurrency: string, currency: string) => {
  if (amount == null || amount === '' || Number(amount) === 0) return i18n.FundH5.FeeFree;
  const unit = String(chargeCurrency || currency || '').trim();
  return unit ? `${amount} ${unit}` : String(amount);
};

/**
 * 「全部取出」= 可提取金额 − 手续费（同币种时）。
 * 免手续费 / 币种不一致 → 全额；扣完 ≤0 → 空。
 */
export const calcWithdrawAllAmount = (
  bal: unknown,
  chargeAmount: unknown,
  chargeCurrency: string,
  currency: string
): string => {
  if (!isNumericBalance(bal)) return '';
  const balText = String(bal).trim();
  const feeNum = chargeAmount == null || chargeAmount === '' ? 0 : Number(chargeAmount);
  const chargeCur = String(chargeCurrency || '').trim();
  const cur = String(currency || '').trim();
  const sameCurrency = !chargeCur || !cur || chargeCur === cur;
  if (!sameCurrency || !Number.isFinite(feeNum) || feeNum <= 0) {
    return sanitizeWithdrawAmount(balText);
  }
  const net = Number(balText) - feeNum;
  if (!Number.isFinite(net) || net <= 0) return '';
  return sanitizeWithdrawAmount(net.toFixed(3));
};
